import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import CpNumberIndicator from 'src/components/CpNumberIndicator';
import { SHORT_CODE_REASON } from 'src/components/LineTypeBadge';
import helpers from 'src/helpers';
import { getBlockedRegion, getBlockedRegionLabel } from 'src/constants/blockedRegions';
import {
  hasUserTextableOverride,
  isPhoneNonTextable,
} from 'src/views/management/ChatView/contactPhones';

/**
 * Builds tooltip text for non-textable phones.
 * Format: "DisplayLineType • Carrier • Cannot receive texts"
 *
 * CON-1896 — a number is non-textable here for one of three reasons, and the
 * tooltip names the one that applies: a blocked region, the consultant's own
 * not-textable override, or the carrier lookup.
 */
function buildNonTextableTooltip(phone, phoneInfo) {
  const blockedRegion = getBlockedRegion(phone);
  if (blockedRegion.blocked) {
    return `${getBlockedRegionLabel(blockedRegion.country)} number • Cannot receive texts from CP`;
  }
  if (hasUserTextableOverride(phoneInfo)) {
    return 'You marked this number as not textable';
  }
  // CON-2010
  if (phoneInfo?.isShortCode) {
    return SHORT_CODE_REASON;
  }

  const parts = [];

  // Use displayLineType (user-friendly) if available, fallback to lineType
  if (phoneInfo.displayLineType) {
    parts.push(phoneInfo.displayLineType);
  } else if (phoneInfo.lineType) {
    parts.push(phoneInfo.lineType);
  } else {
    parts.push('Landline');
  }

  // Add carrier (show "Unknown Carrier" if not available)
  parts.push(phoneInfo.carrier || 'Unknown Carrier');

  parts.push('Cannot receive texts');

  return parts.join(' • ');
}

/**
 * PhonesList - Displays phone numbers with non-textable warnings
 *
 * Shows a warning icon for phones that cannot receive texts (landlines).
 * Textable phones appear normal to keep the UI clean.
 */
function PhonesList({ phoneNumbers, phoneInfoList = [] }) {
  return (
    <Grid
      container
      spacing={1}
      sx={{
        marginBottom: '8px',
        alignItems: 'center',
        color: '#AC9CF1'
      }}
    >
      {!helpers.isEmpty(phoneNumbers) && phoneNumbers.map((phone, index) => {
        const phoneInfo = phoneInfoList[index];
        // CON-1896 — shared with the chat page's other phone surfaces, so the
        // consultant's textable override counts here as it does on the
        // contact page.
        const isNonTextable = isPhoneNonTextable(phone, phoneInfo);
        const isCpNumber = phoneInfo?.isCpNumber === true;

        return (
          <React.Fragment key={phone}>
            {index > 0 && '•'}
            <Grid>
              {isNonTextable ? (
                <Tooltip
                  title={buildNonTextableTooltip(phone, phoneInfo)}
                  placement="top"
                >
                  <Chip
                    size="small"
                    icon={<PhoneDisabledIcon sx={{ color: '#F44336 !important' }} />}
                    label={(
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {helpers.transform.toPhoneNumber(phone)}
                        {isCpNumber && <CpNumberIndicator sx={{ ml: 0.5 }} />}
                      </span>
                    )}
                    sx={{
                      padding: '3px',
                      fontSize: '12px',
                      backgroundColor: '#FFEBEE',
                      '& .MuiChip-icon': {
                        color: '#F44336',
                      },
                    }}
                  />
                </Tooltip>
              ) : (
                <Chip
                  size="small"
                  icon={<LocalPhoneOutlinedIcon />}
                  label={(
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {helpers.transform.toPhoneNumber(phone)}
                      {isCpNumber && <CpNumberIndicator sx={{ ml: 0.5 }} />}
                    </span>
                  )}
                  sx={{
                    padding: '3px',
                    fontSize: '12px'
                  }}
                />
              )}
            </Grid>
          </React.Fragment>
        );
      })}
    </Grid>
  );
}

PhonesList.propTypes = {
  phoneNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  phoneInfoList: PropTypes.arrayOf(PropTypes.shape({
    lineType: PropTypes.string,
    displayLineType: PropTypes.string,
    isTextable: PropTypes.bool,
    carrier: PropTypes.string,
    checkedAtRelative: PropTypes.string,
    isCpNumber: PropTypes.bool,
    userTextableOverride: PropTypes.bool,
  })),
};

export default PhonesList;
